import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  AVAILABILITY_ENTITIES,
  AVAILABILITY_TYPES,
  availabilityTypeMeta,
  createCoreAvailabilityRule,
  deleteCoreAvailabilityRule,
  findAvailabilityConflict,
  listCoreAvailabilityRules,
  updateCoreAvailabilityRule,
  WEEKDAYS,
  weekdayLabel,
  type CoreAvailabilityRule,
} from '@/lib/coreAvailability';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

interface FormState {
  id: number | null;
  ruleType: string;
  entityType: string;
  label: string;
  weekday: number | null;
  startTime: string;
  endTime: string;
  startDate: string;
  endDate: string;
  exclusive: boolean;
}

const EMPTY: FormState = {
  id: null, ruleType: 'availability', entityType: 'organization', label: '',
  weekday: 1, startTime: '09:00', endTime: '17:00', startDate: '', endDate: '', exclusive: true,
};

export default function AvailabilityTab({ org }: Props) {
  const [rules, setRules] = useState<CoreAvailabilityRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRules(await listCoreAvailabilityRules(org.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setConflict(''); setOpen(true); };
  const openEdit = (r: CoreAvailabilityRule) => {
    setForm({
      id: r.id, ruleType: r.rule_type, entityType: r.entity_type || 'organization', label: r.label || '',
      weekday: r.weekday, startTime: r.start_time || '', endTime: r.end_time || '',
      startDate: r.start_date || '', endDate: r.end_date || '', exclusive: r.exclusive ?? true,
    });
    setConflict('');
    setOpen(true);
  };

  const save = async () => {
    setConflict('');
    const candidate = { rule_type: form.ruleType, weekday: form.weekday, start_time: form.startTime, end_time: form.endTime };
    const clash = findAvailabilityConflict(candidate, rules, form.id ?? undefined);
    if (clash) {
      setConflict(`Conflit avec « ${clash.label || availabilityTypeMeta(clash.rule_type).label} » (${weekdayLabel(clash.weekday)} ${clash.start_time}-${clash.end_time}). Un créneau exclusif se chevauche déjà.`);
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await updateCoreAvailabilityRule(form.id, {
          ruleType: form.ruleType, entityType: form.entityType, label: form.label || null,
          weekday: form.weekday, startTime: form.startTime, endTime: form.endTime,
          startDate: form.startDate || null, endDate: form.endDate || null, exclusive: form.exclusive,
        });
      } else {
        await createCoreAvailabilityRule({
          tenantId: org.tenant_id, organizationId: org.id, entityType: form.entityType,
          ruleType: form.ruleType, label: form.label || null, weekday: form.weekday,
          startTime: form.startTime, endTime: form.endTime, startDate: form.startDate || null,
          endDate: form.endDate || null, exclusive: form.exclusive,
        });
      }
      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: CoreAvailabilityRule) => {
    setBusyId(r.id);
    try { await deleteCoreAvailabilityRule(r.id); load(); } finally { setBusyId(null); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <p className="text-xs text-foreground-500 max-w-2xl">
          Couche Availability centrale : disponibilités, horaires, pauses, congés, fermetures, réservations et blocages manuels.
          Le socle <span className="font-semibold text-foreground-700">empêche les conflits</span> entre créneaux exclusifs.
        </p>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i> Nouvelle règle
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {AVAILABILITY_TYPES.map((t) => {
          const count = rules.filter((r) => r.rule_type === t.key).length;
          return (
            <span key={t.key} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${t.className}`}>
              <i className={t.icon}></i>{t.label} · {count}
            </span>
          );
        })}
      </div>

      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : rules.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-time-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucune règle de disponibilité.</p>
          <p className="text-xs text-foreground-400 mt-1">Exemple : Professionnel 09:00 → 17:00, Pause 13:00 → 14:00.</p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-background-100 text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
            <span className="col-span-3">Type</span>
            <span className="col-span-2">Cible</span>
            <span className="col-span-2">Jour</span>
            <span className="col-span-3">Créneau</span>
            <span className="col-span-2 text-right">Actions</span>
          </div>
          {rules.map((r) => {
            const meta = availabilityTypeMeta(r.rule_type);
            return (
              <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-background-200/60 items-center">
                <div className="col-span-3 flex items-center gap-2 min-w-0">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${meta.className}`}><i className={meta.icon}></i>{meta.label}</span>
                </div>
                <span className="col-span-2 text-xs text-foreground-600 truncate">{r.label || AVAILABILITY_ENTITIES.find((e) => e.key === r.entity_type)?.label || 'Entreprise'}</span>
                <span className="col-span-2 text-xs text-foreground-600">{r.weekday != null ? weekdayLabel(r.weekday) : (r.start_date || '—')}</span>
                <span className="col-span-3 text-xs text-foreground-700 tabular-nums">{r.start_time || '—'} → {r.end_time || '—'}</span>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <button onClick={() => openEdit(r)} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"><i className="ri-edit-line"></i></button>
                  <button onClick={() => remove(r)} disabled={busyId === r.id} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal
          title={form.id ? 'Modifier la règle' : 'Nouvelle règle de disponibilité'}
          onClose={() => setOpen(false)}
          maxWidth="max-w-xl"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            {conflict && <div className="p-3 bg-accent-100 border border-accent-200 rounded-md text-xs text-accent-900"><i className="ri-error-warning-line mr-1"></i>{conflict}</div>}
            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-1">Type de règle</span>
              <div className="flex flex-wrap gap-2">
                {AVAILABILITY_TYPES.map((t) => (
                  <button key={t.key} type="button" onClick={() => setForm({ ...form, ruleType: t.key })} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors cursor-pointer ${form.ruleType === t.key ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-background-200/70 text-foreground-600 hover:bg-background-100'}`}>
                    <i className={t.icon}></i>{t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Cible</span>
                <select className={INPUT} value={form.entityType} onChange={(e) => setForm({ ...form, entityType: e.target.value })}>
                  {AVAILABILITY_ENTITIES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Jour</span>
                <select className={INPUT} value={form.weekday ?? ''} onChange={(e) => setForm({ ...form, weekday: e.target.value === '' ? null : Number(e.target.value) })}>
                  <option value="">Aucun (dates)</option>
                  {WEEKDAYS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                </select>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Heure de début</span>
                <input type="time" className={INPUT} value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Heure de fin</span>
                <input type="time" className={INPUT} value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Date de début (option)</span>
                <input type="date" className={INPUT} value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Date de fin (option)</span>
                <input type="date" className={INPUT} value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Libellé (option)</span>
              <input className={INPUT} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex : Rendez-vous client" />
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.exclusive} onChange={(e) => setForm({ ...form, exclusive: e.target.checked })} className="w-4 h-4 rounded accent-primary-500" />
              <span className="text-xs text-foreground-600">Créneau exclusif (bloque les réservations qui se chevauchent)</span>
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}